import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  GitCompare, 
  X,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Info,
  Copy,
  Database,
  Settings,
  Loader2,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  AlertTriangle,
  Check
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const EntityCompareModal = ({ entities, open, onOpenChange, sourceTargetPairs }) => {
  const [pairA, setPairA] = useState(null);
  const [pairB, setPairB] = useState(null);
  const [pairADetails, setPairADetails] = useState(null);
  const [pairBDetails, setPairBDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedRecords, setExpandedRecords] = useState({});
  const [expandedJsons, setExpandedJsons] = useState({});
  const [differenceCategories, setDifferenceCategories] = useState({}); // expected vs problematic
  const [entityASelection, setEntityASelection] = useState("all");
  const [entityBSelection, setEntityBSelection] = useState("all");
  const [availableEntitiesA, setAvailableEntitiesA] = useState([]);
  const [availableEntitiesB, setAvailableEntitiesB] = useState([]);

  // Get unique source-target pairs from entities
  const getSourceTargetPairs = () => {
    if (!entities) return [];
    const pairs = new Map();
    
    entities.forEach(entity => {
      const key = `${entity.source}-${entity.target}`;
      if (!pairs.has(key)) {
        pairs.set(key, {
          source: entity.source,
          target: entity.target,
          label: `${entity.source} → ${entity.target}`,
          entityCount: 1
        });
      } else {
        pairs.get(key).entityCount++;
      }
    });
    
    return Array.from(pairs.values());
  };

  const fetchPairDetails = async (source, target, selectedEntity = null) => {
    try {
      let url = `${API}/api/source-target-pair-details/${source}/${target}`;
      if (selectedEntity) {
        url += `?entity=${encodeURIComponent(selectedEntity)}`;
      }
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching pair details:', error);
      toast.error('Failed to fetch pair details');
      return null;
    }
  };

  const fetchAvailableEntities = async (source, target) => {
    try {
      const response = await axios.get(`${API}/api/source-target-pair-entities/${source}/${target}`);
      return response.data.entities || [];
    } catch (error) {
      console.error('Error fetching available entities:', error);
      return [];
    }
  };

  const handlePairAChange = async (value) => {
    const pair = getSourceTargetPairs().find(p => `${p.source}-${p.target}` === value);
    setPairA(pair);
    setEntityASelection("all");
    setPairADetails(null);
    
    if (pair) {
      setLoading(true);
      // Fetch available entities for this pair
      const entities = await fetchAvailableEntities(pair.source, pair.target);
      setAvailableEntitiesA(entities);
      
      // Fetch details without entity filter initially
      const details = await fetchPairDetails(pair.source, pair.target);
      setPairADetails(details);
      setLoading(false);
    }
  };

  const handlePairBChange = async (value) => {
    const pair = getSourceTargetPairs().find(p => `${p.source}-${p.target}` === value);
    setPairB(pair);
    setEntityBSelection("all");
    setPairBDetails(null);
    
    if (pair) {
      setLoading(true);
      // Fetch available entities for this pair
      const entities = await fetchAvailableEntities(pair.source, pair.target);
      setAvailableEntitiesB(entities);
      
      // Fetch details without entity filter initially
      const details = await fetchPairDetails(pair.source, pair.target);
      setPairBDetails(details);
      setLoading(false);
    }
  };

  const handleEntityAChange = async (value) => {
    setEntityASelection(value);
    if (pairA && value && value !== "all") {
      setLoading(true);
      const details = await fetchPairDetails(pairA.source, pairA.target, value);
      setPairADetails(details);
      setLoading(false);
    } else if (pairA && (value === "all" || !value)) {
      // If "all" entities selected, fetch all
      setLoading(true);
      const details = await fetchPairDetails(pairA.source, pairA.target);
      setPairADetails(details);
      setLoading(false);
    }
  };

  const handleEntityBChange = async (value) => {
    setEntityBSelection(value);
    if (pairB && value && value !== "all") {
      setLoading(true);
      const details = await fetchPairDetails(pairB.source, pairB.target, value);
      setPairBDetails(details);
      setLoading(false);
    } else if (pairB && (value === "all" || !value)) {
      // If "all" entities selected, fetch all
      setLoading(true);
      const details = await fetchPairDetails(pairB.source, pairB.target);
      setPairBDetails(details);
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleRecord = (tableType, recordId) => {
    const key = `${tableType}-${recordId}`;
    setExpandedRecords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleJson = (jsonKey) => {
    setExpandedJsons(prev => ({
      ...prev,
      [jsonKey]: !prev[jsonKey]
    }));
  };

  const markDifference = (diffKey, category) => {
    setDifferenceCategories(prev => ({
      ...prev,
      [diffKey]: category // 'expected' or 'problematic'
    }));
  };

  // Record matching logic
  const matchRecords = (tableType, recordsA, recordsB) => {
    const matches = [];
    const unmatchedA = [...recordsA];
    const unmatchedB = [...recordsB];

    if (tableType === 'migration_sources' || tableType === 'migration_targets') {
      // Match by migrationEntity relationship from migration_entities
      recordsA.forEach((recordA, indexA) => {
        const matchIndex = recordsB.findIndex(recordB => 
          recordB.migrationEntity === recordA.migrationEntity ||
          recordB.id === recordA.id
        );
        
        if (matchIndex !== -1) {
          matches.push({
            recordA,
            recordB: recordsB[matchIndex],
            matchType: 'matched',
            indexA,
            indexB: matchIndex
          });
          unmatchedA.splice(unmatchedA.findIndex(r => r.id === recordA.id), 1);
          unmatchedB.splice(unmatchedB.findIndex(r => r.id === recordsB[matchIndex].id), 1);
        }
      });
    } else {
      // Match by source and target for other tables
      recordsA.forEach((recordA, indexA) => {
        const matchIndex = recordsB.findIndex(recordB => 
          recordB.source === recordA.source && recordB.target === recordA.target
        );
        
        if (matchIndex !== -1) {
          matches.push({
            recordA,
            recordB: recordsB[matchIndex],
            matchType: 'matched',
            indexA,
            indexB: matchIndex
          });
          unmatchedA.splice(unmatchedA.findIndex(r => r.id === recordA.id), 1);
          unmatchedB.splice(unmatchedB.findIndex(r => r.id === recordsB[matchIndex].id), 1);
        }
      });
    }

    // Add unmatched records
    unmatchedA.forEach((record, index) => {
      matches.push({
        recordA: record,
        recordB: null,
        matchType: 'onlyInA',
        indexA: recordsA.findIndex(r => r.id === record.id),
        indexB: null
      });
    });

    unmatchedB.forEach((record, index) => {
      matches.push({
        recordA: null,
        recordB: record,
        matchType: 'onlyInB',
        indexA: null,
        indexB: recordsB.findIndex(r => r.id === record.id)
      });
    });

    return matches;
  };

  // Field comparison logic
  const compareFields = (recordA, recordB, tableType) => {
    const differences = [];
    
    if (!recordA && !recordB) return differences;
    
    const fieldsA = recordA ? Object.keys(recordA) : [];
    const fieldsB = recordB ? Object.keys(recordB) : [];
    const allFields = [...new Set([...fieldsA, ...fieldsB])];

    allFields.forEach(field => {
      const valueA = recordA ? recordA[field] : undefined;
      const valueB = recordB ? recordB[field] : undefined;
      
      const isDifferent = JSON.stringify(valueA) !== JSON.stringify(valueB);
      const isMissing = (recordA && !recordB) || (!recordA && recordB) || 
                       (recordA && valueA === undefined) || (recordB && valueB === undefined);
      
      if (isDifferent || isMissing) {
        differences.push({
          field,
          valueA: valueA,
          valueB: valueB,
          type: isMissing ? 'missing' : 'different',
          isJson: typeof valueA === 'object' || typeof valueB === 'object'
        });
      }
    });

    return differences;
  };

  const renderJsonDiff = (valueA, valueB, fieldName) => {
    const jsonA = valueA ? (typeof valueA === 'string' ? valueA : JSON.stringify(valueA, null, 2)) : 'null';
    const jsonB = valueB ? (typeof valueB === 'string' ? valueB : JSON.stringify(valueB, null, 2)) : 'null';
    
    return (
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <div className="font-medium text-blue-800 mb-1 text-xs">Entity A - {fieldName}</div>
          <pre className="text-xs bg-white p-2 rounded border max-h-32 overflow-auto font-mono">
            {jsonA}
          </pre>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-2">
          <div className="font-medium text-green-800 mb-1 text-xs">Entity B - {fieldName}</div>
          <pre className="text-xs bg-white p-2 rounded border max-h-32 overflow-auto font-mono">
            {jsonB}
          </pre>
        </div>
      </div>
    );
  };

  const renderFieldComparison = (differences, recordMatch, tableType) => {
    if (differences.length === 0) {
      return (
        <div className="flex items-center text-green-600 text-sm py-2">
          <CheckCircle className="w-4 h-4 mr-2" />
          All fields match
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {differences.map((diff, index) => {
          const diffKey = `${tableType}-${recordMatch.recordA?.id || recordMatch.recordB?.id}-${diff.field}`;
          const category = differenceCategories[diffKey];
          
          return (
            <div key={index} className="border rounded p-2 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-slate-700 text-sm">{diff.field}</span>
                  {diff.type === 'missing' ? (
                    <Badge variant="destructive" className="text-xs px-1 py-0">Missing</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs px-1 py-0">Different</Badge>
                  )}
                  {category && (
                    <Badge 
                      variant={category === 'expected' ? 'secondary' : 'destructive'} 
                      className="text-xs px-1 py-0"
                    >
                      {category}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => markDifference(diffKey, 'expected')}
                    className={`text-xs px-2 py-1 h-6 ${category === 'expected' ? 'bg-blue-50' : ''}`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Expected
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => markDifference(diffKey, 'problematic')}
                    className={`text-xs px-2 py-1 h-6 ${category === 'problematic' ? 'bg-red-50' : ''}`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Issue
                  </Button>
                </div>
              </div>
              
              {diff.isJson ? (
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleJson(`${diffKey}-json`)}
                    className="text-xs mb-2 h-6 px-2"
                  >
                    {expandedJsons[`${diffKey}-json`] ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                    {expandedJsons[`${diffKey}-json`] ? 'Hide JSON' : 'Show JSON'}
                  </Button>
                  {expandedJsons[`${diffKey}-json`] && renderJsonDiff(diff.valueA, diff.valueB, diff.field)}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-xs font-medium text-blue-800 mb-1">Entity A</div>
                    <div className="text-xs break-all">{diff.valueA !== undefined ? String(diff.valueA) : 'undefined'}</div>
                  </div>
                  <div className="p-2 bg-green-50 border border-green-200 rounded">
                    <div className="text-xs font-medium text-green-800 mb-1">Entity B</div>
                    <div className="text-xs break-all">{diff.valueB !== undefined ? String(diff.valueB) : 'undefined'}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableComparison = (tableType, dataA, dataB) => {
    if (!dataA && !dataB) return null;
    
    const recordsA = dataA || [];
    const recordsB = dataB || [];
    const matches = matchRecords(tableType, recordsA, recordsB);
    
    const sectionKey = tableType;
    const isExpanded = expandedSections[sectionKey];

    return (
      <Card className="bg-white border border-slate-200 mb-3">
        <CardHeader 
          className="cursor-pointer py-3"
          onClick={() => toggleSection(sectionKey)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4" />
              <span className="capitalize text-sm">{tableType.replace('_', ' ')}</span>
              <Badge variant="outline" className="ml-2 text-xs">
                {matches.length} {matches.length === 1 ? 'record' : 'records'}
              </Badge>
            </div>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </CardTitle>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="space-y-3 pt-0">
            {matches.map((match, index) => {
              const recordKey = `${tableType}-${match.recordA?.id || match.recordB?.id || index}`;
              const isRecordExpanded = expandedRecords[recordKey];
              const differences = compareFields(match.recordA, match.recordB, tableType);
              
              return (
                <div key={index} className="border rounded p-3 bg-slate-50">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleRecord(tableType, match.recordA?.id || match.recordB?.id || index)}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        {match.matchType === 'matched' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {match.matchType === 'onlyInA' && <AlertCircle className="w-4 h-4 text-orange-600" />}
                        {match.matchType === 'onlyInB' && <AlertCircle className="w-4 h-4 text-blue-600" />}
                        <span className="font-medium text-sm">
                          Record {match.recordA?.id || match.recordB?.id || index + 1}
                        </span>
                      </div>
                      
                      {match.matchType === 'matched' && (
                        <Badge variant={differences.length > 0 ? 'destructive' : 'secondary'} className="text-xs">
                          {differences.length === 0 ? 'Identical' : `${differences.length} differences`}
                        </Badge>
                      )}
                      {match.matchType === 'onlyInA' && <Badge variant="outline" className="text-xs">Only in Entity A</Badge>}
                      {match.matchType === 'onlyInB' && <Badge variant="outline" className="text-xs">Only in Entity B</Badge>}
                    </div>
                    {isRecordExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                  
                  {isRecordExpanded && (
                    <div className="mt-3">
                      {match.matchType === 'matched' ? (
                        renderFieldComparison(differences, match, tableType)
                      ) : (
                        <div className="p-2 bg-white rounded border">
                          <div className="text-sm text-slate-600 mb-2">
                            {match.matchType === 'onlyInA' ? 'This record exists only in Entity A' : 'This record exists only in Entity B'}
                          </div>
                          <pre className="text-xs bg-slate-100 p-2 rounded max-h-40 overflow-auto font-mono">
                            {JSON.stringify(match.recordA || match.recordB, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        )}
      </Card>
    );
  };

  const copyComparison = () => {
    if (!pairADetails || !pairBDetails) return;
    
    const comparison = {
      entityA: {
        source: pairA.source,
        target: pairA.target,
        summary: {
          entities: pairADetails.migration_entities.length,
          sources: pairADetails.migration_sources.length,
          targets: pairADetails.migration_targets.length,
          connectionFields: pairADetails.target_connection_fields.length,
          validations: pairADetails.validate_source_data.length
        }
      },
      entityB: {
        source: pairB.source,
        target: pairB.target,
        summary: {
          entities: pairBDetails.migration_entities.length,
          sources: pairBDetails.migration_sources.length,
          targets: pairBDetails.migration_targets.length,
          connectionFields: pairBDetails.target_connection_fields.length,
          validations: pairBDetails.validate_source_data.length
        }
      },
      differences: Object.keys(differenceCategories).reduce((acc, key) => {
        acc[key] = differenceCategories[key];
        return acc;
      }, {}),
      timestamp: new Date().toISOString()
    };

    navigator.clipboard.writeText(JSON.stringify(comparison, null, 2));
    toast.success('Detailed comparison report copied to clipboard');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-hidden bg-white text-slate-900 flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-2 text-slate-900">
            <GitCompare className="w-5 h-5" />
            <span>Compare Source-Target Pairs - Detailed Analysis</span>
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Field-by-field comparison of complete connector configurations across all database tables
          </DialogDescription>
        </DialogHeader>

        {/* Pair Selection - Fixed */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg flex-shrink-0">
          <div className="space-y-3">
            <label className="block text-sm font-medium mb-2 text-slate-700">Entity A (Source-Target Pair)</label>
            <Select onValueChange={handlePairAChange}>
              <SelectTrigger className="bg-white text-slate-900">
                <SelectValue placeholder="Select first pair to compare..." />
              </SelectTrigger>
              <SelectContent className="bg-white text-slate-900">
                {getSourceTargetPairs().map(pair => (
                  <SelectItem key={`${pair.source}-${pair.target}`} value={`${pair.source}-${pair.target}`}>
                    {pair.label} ({pair.entityCount} entities)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Entity A Selection */}
            {pairA && availableEntitiesA.length > 0 && (
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-600">Select Entity (Optional)</label>
                <Select onValueChange={handleEntityAChange} value={entityASelection}>
                  <SelectTrigger className="bg-white text-slate-900 h-8 text-xs">
                    <SelectValue placeholder="All entities (or select specific)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-900">
                    <SelectItem value="all">All entities</SelectItem>
                    {availableEntitiesA.map(entity => (
                      <SelectItem key={entity} value={entity}>
                        {entity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {pairA && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                <strong className="text-blue-800">Selected:</strong> <span className="text-blue-700">{pairA.label}</span>
                {entityASelection && entityASelection !== "all" && <div className="text-xs text-blue-600 mt-1">Entity: {entityASelection}</div>}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium mb-2 text-slate-700">Entity B (Source-Target Pair)</label>
            <Select onValueChange={handlePairBChange}>
              <SelectTrigger className="bg-white text-slate-900">
                <SelectValue placeholder="Select second pair to compare..." />
              </SelectTrigger>
              <SelectContent className="bg-white text-slate-900">
                {getSourceTargetPairs().map(pair => (
                  <SelectItem key={`${pair.source}-${pair.target}`} value={`${pair.source}-${pair.target}`}>
                    {pair.label} ({pair.entityCount} entities)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Entity B Selection */}
            {pairB && availableEntitiesB.length > 0 && (
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-600">Select Entity (Optional)</label>
                <Select onValueChange={handleEntityBChange} value={entityBSelection}>
                  <SelectTrigger className="bg-white text-slate-900 h-8 text-xs">
                    <SelectValue placeholder="All entities (or select specific)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-900">
                    <SelectItem value="all">All entities</SelectItem>
                    {availableEntitiesB.map(entity => (
                      <SelectItem key={entity} value={entity}>
                        {entity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {pairB && (
              <div className="p-2 bg-green-50 border border-green-200 rounded text-sm">
                <strong className="text-green-800">Selected:</strong> <span className="text-green-700">{pairB.label}</span>
                {entityBSelection && entityBSelection !== "all" && <div className="text-xs text-green-600 mt-1">Entity: {entityBSelection}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center p-8 bg-white flex-shrink-0">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-700">Loading comparison data...</span>
          </div>
        )}

        {/* Scrollable Comparison Content */}
        {!loading && pairA && pairB && pairADetails && pairBDetails && (
          <div className="flex-1 min-h-0 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
            <div className="space-y-4 pr-4">
              {/* Summary Overview */}
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span>Comparison Summary</span>
                    <Button onClick={copyComparison} size="sm" variant="outline">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Report
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <h3 className="font-semibold text-blue-800 mb-2 text-sm">Entity A: {pairA.label}</h3>
                      <div className="text-xs text-blue-700 space-y-1">
                        <div>Entities: {pairADetails.migration_entities.length}</div>
                        <div>Sources: {pairADetails.migration_sources.length}</div>
                        <div>Targets: {pairADetails.migration_targets.length}</div>
                        <div>Connections: {pairADetails.target_connection_fields.length}</div>
                        <div>Validations: {pairADetails.validate_source_data.length}</div>
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <h3 className="font-semibold text-green-800 mb-2 text-sm">Entity B: {pairB.label}</h3>
                      <div className="text-xs text-green-700 space-y-1">
                        <div>Entities: {pairBDetails.migration_entities.length}</div>
                        <div>Sources: {pairBDetails.migration_sources.length}</div>
                        <div>Targets: {pairBDetails.migration_targets.length}</div>
                        <div>Connections: {pairBDetails.target_connection_fields.length}</div>
                        <div>Validations: {pairBDetails.validate_source_data.length}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Table-by-Table Comparisons */}
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-slate-800">Detailed Field-by-Field Analysis</h2>
                
                {renderTableComparison('migration_entities', pairADetails.migration_entities, pairBDetails.migration_entities)}
                
                {renderTableComparison('migration_sources', pairADetails.migration_sources, pairBDetails.migration_sources)}
                
                {renderTableComparison('migration_targets', pairADetails.migration_targets, pairBDetails.migration_targets)}
                
                {renderTableComparison('target_connection_fields', pairADetails.target_connection_fields, pairBDetails.target_connection_fields)}
                
                {renderTableComparison('validate_source_data', pairADetails.validate_source_data, pairBDetails.validate_source_data)}
              </div>
            </div>
          </div>
        )}

        {/* Fixed Footer */}
        <div className="flex justify-end pt-3 border-t border-slate-200 bg-white flex-shrink-0">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="text-slate-700 border-slate-300 bg-white hover:bg-slate-50">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntityCompareModal;